import { ViewBooking } from "collins-js-sdk";
import React, { useState } from "react";
import { Check, Copy } from "lucide-react";
import { DiaryContextType } from "../../../../../context/DiaryContext";
import { getBookingStatus } from "../../../../../utils/singleBooking";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { SelectedBookingTabEnum } from "../Tabs/SelectedBookingTabsController";

export const getBookingStatusIcon = (selectedBooking?: ViewBooking) => {
  if (!selectedBooking) {
    return <></>;
  }

  const bookingStatus = getBookingStatus(selectedBooking);
  return (
    <EDSBadge
      variant="default"
      className="booking-status-badge"
      style={
        {
          "--booking-type-color": bookingStatus.colour,
          "--booking-type-foreground": bookingStatus.labelColour
        } as React.CSSProperties
      }
    >
      <span className="booking-status-badge-text">{bookingStatus.label}</span>
    </EDSBadge>
  );
};

export const getBookingRefHyperlink = (
  booking: ViewBooking | undefined,
  diaryContext: DiaryContextType,
  setSelectedTabKey: ((value: SelectedBookingTabEnum) => void) | undefined,
  selectedTabKey?: SelectedBookingTabEnum
) => {
  const { selectedBooking, setDiaryContext, bookings } = diaryContext;
  const isCurrentBooking =
    selectedBooking && selectedBooking.reference === booking?.reference;

  const selectBooking = () => {
    if (isCurrentBooking) {
      return;
    }

    if (setSelectedTabKey) {
      setSelectedTabKey(selectedTabKey ?? SelectedBookingTabEnum.Manage);
    }

    const stateBooking = bookings?.find((b) => b.id === booking?.id);
    setDiaryContext({
      selectedBooking: stateBooking ?? booking,
      selectedBookingV4: undefined
    });
  };

  const fullReference = `DMN-${booking?.reference ?? ""}`;

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className={[
          "bg-transparent border-none p-0 cursor-pointer text-[var(--primaryColor)] underline",
          isCurrentBooking
            ? "DisableHyperlink no-underline opacity-50 cursor-default"
            : ""
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={selectBooking}
      >
        {fullReference}
      </button>
      <CopyRefButton reference={fullReference} />
    </span>
  );
};

const CopyRefButton: React.FC<{ reference: string }> = ({ reference }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is unavailable
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="bg-transparent border-none p-0 cursor-pointer inline-flex items-center text-[var(--primaryColor)] hover:opacity-70"
      aria-label={
        copied ? `Copied ${reference}` : `Copy ${reference} to clipboard`
      }
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
};
